import { useState, useEffect, type InputHTMLAttributes } from 'react';

interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max'> {
  /** Current value, or null when the field is empty (allowEmpty only). */
  value: number | null | undefined;
  /** Fired with the parsed value while typing, and the clamped value on blur. */
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Whole numbers only: a typed decimal is rounded on blur. */
  integer?: boolean;
  /** When true, clearing the field yields null. Otherwise an empty field on
   *  blur goes back to the value it had, so nothing empty is ever persisted. */
  allowEmpty?: boolean;
  /** Visual error state (red border), mirrors `Input`. */
  error?: boolean;
}

/**
 * Controlled number `<input>` that is actually editable: the user can clear the
 * field and retype. Converting or clamping on every keystroke makes a field
 * fight its user — clearing it reads as 0 and snaps back to the minimum (or a
 * `|| default`), so typing "3" over "1" gives "13". Here the typed text is kept
 * as-is while focused; valid numbers are passed up live, and the value is
 * clamped to [min, max] when focus leaves (or Enter is pressed). Styling is
 * delegated via `className`/props, so it drops into existing forms unchanged.
 */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  integer = false,
  allowEmpty = false,
  error,
  className = '',
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: NumberInputProps) {
  const shown = (v: number | null | undefined) => (v == null ? '' : String(v));
  // Local draft lets the field hold transient states ("", "0.", "-") while
  // typing without forcing them onto the parent's model every keystroke.
  const [draft, setDraft] = useState<string>(shown(value));
  const [focused, setFocused] = useState(false);

  // Re-sync the draft from the external value when not actively editing (e.g.
  // the parent reset the form, or clamped the value elsewhere).
  useEffect(() => {
    if (!focused) setDraft(shown(value));
  }, [value, focused]);

  const parse = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const clamp = (n: number) => {
    let v = integer ? Math.round(n) : n;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };

  return (
    <input
      {...rest}
      type="number"
      min={min}
      max={max}
      className={`${error ? 'ui-input--error ' : ''}${className}`.trim() || undefined}
      value={draft}
      onFocus={e => { setFocused(true); onFocus?.(e); }}
      onChange={e => {
        const raw = e.target.value;
        setDraft(raw);
        const n = parse(raw);
        // Pass valid numbers up live (unclamped — the user may be mid-way to an
        // in-range value). A cleared field is passed up only when empties are
        // allowed; otherwise the parent keeps its last value until blur.
        if (n != null) onChange(integer ? Math.round(n) : n);
        else if (raw === '' && allowEmpty) onChange(null);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
        onKeyDown?.(e);
      }}
      onBlur={e => {
        setFocused(false);
        const n = parse(draft);
        if (n == null) {
          if (allowEmpty) { onChange(null); setDraft(''); }
          else {
            // Back to the value it had: an empty field is not a request for 0.
            const v = value != null ? value : clamp(min ?? 0);
            onChange(v);
            setDraft(String(v));
          }
        } else {
          const v = clamp(n);
          onChange(v);
          setDraft(String(v));
        }
        onBlur?.(e);
      }}
    />
  );
}

/** {@link NumberInput} for whole numbers. */
export function IntegerInput(props: Omit<NumberInputProps, 'integer'>) {
  return <NumberInput {...props} integer />;
}
